/**
 * SessionTimeoutProvider
 * Wraps authenticated app content and automatically logs the user out after inactivity.
 * Uses InactivityTimer (pure class, no React deps) so activity detection is fully testable.
 */

import { useEffect, useRef } from 'react'
import { useSelector, useDispatch, useTranslation, showToast } from '@ufc/core'

import { authActions } from '../../store/auth.slice'
import { selectIsAuthenticated } from '../../store/auth.selectors'
import { InactivityTimer } from '../../domain/utils/inactivity-timer'
import { isPersistentSession } from '../../domain/utils/token-utils'

export interface SessionTimeoutProviderProps {
  timeoutMs: number
  children: React.ReactNode
}

export function SessionTimeoutProvider({ timeoutMs, children }: SessionTimeoutProviderProps) {
  const dispatch = useDispatch()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const { t } = useTranslation()
  const hasTimedOutRef = useRef(false)

  useEffect(() => {
    if (isAuthenticated) {
      hasTimedOutRef.current = false
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (!isAuthenticated) return
    if (isPersistentSession()) return

    const timer = new InactivityTimer({
      timeoutMs,
      onTimeout: () => {
        if (hasTimedOutRef.current) return
        hasTimedOutRef.current = true

        showToast({
          title: t('auth.sessionExpired'),
          description: t('auth.sessionExpiredMessage'),
          variant: 'destructive',
          duration: 10000,
          className: 'text-base [&_[toast-title]]:text-lg [&_[toast-title]]:font-bold md:max-w-[520px]',
        })
        dispatch(authActions.logout())
      },
    })

    timer.start()
    return () => timer.stop()
  }, [isAuthenticated, timeoutMs, dispatch, t])

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>
}
